import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Layers3, ScanEye, Plus, Trash2, X, Filter, CheckCheck, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

// Phase 90: every approve/reject decision needs a written rationale (stored permanently)
const MIN_RATIONALE = 10;
import { PageHeader, StatusBadge, EmptyState } from "../../components/common";
import { StaffAvatar, StaffLabel } from "../../components/StaffAvatar";
import { useParams as useRouteParams } from "react-router-dom";

// Phase 86: "By <name>" with the submitter's photo. Names are denormalised on the case; resolve via id.
function ByStaff({ userId, name, empCode, size = "xs", className = "" }) {
  const { code } = useRouteParams();
  if (!name) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 align-middle ${className}`}>
      <StaffAvatar code={code} userId={userId} name={name} size={size} />
      <StaffLabel code={code} userId={userId} name={name} empCode={empCode} />
    </span>
  );
}
import { PaginationBar, usePageSlice, CardListSkeleton } from "../../components/PaginationBar";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { api, errMsg, fmtDate, fmtDateTime } from "../../lib/api";
import { AssessmentComparison } from "../../components/AssessmentComparison";
import { MissingRatesBanner } from "../../components/MissingRatesBanner";
import { TargetProposalsPanel } from "../../components/TargetProposalsPanel";
import { firstAllowedTab, useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice, ViewOnlyBadge } from "../../components/FeatureGate";
import { CertificateChip, CertificateDownloadButton, CertificateVerifyLink } from "../../components/TransferCertificate";  // Phase 124
import { SafQueue } from "../../components/saf/SafQueue";   // Phase 207
import { SafCaseDialog } from "../../components/saf/SafCaseDialog";   // Phase 207

export function ReviewDialog({ code, item, type, onDone, taxHeads, fys }) {
  const [action, setAction] = useState("approve");
  const [remarks, setRemarks] = useState("");
  const [genDemand, setGenDemand] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const curFy = fys.find((f) => f.is_current)?.id || "";
  const defaultHead = taxHeads.find((t) => (t.tax_head_name || "").toLowerCase().includes("propert"))?.id || "";

  useEffect(() => {
    // Priority 1: rate-engine proposal from the collector (per owner+usage group)
    const proposals = item?.proposed_demands || [];
    if (proposals.length > 0) {
      setGenDemand(true);
      setRows(proposals.map((p) => ({
        usage_label: p.usage_label || "",
        area_sqft: null,
        tax_head_id: p.tax_head_id || taxHeads.find((t) => t.tax_code === p.tax_code)?.id || defaultHead,
        financial_year_id: p.financial_year_id || item?.assessment?.financial_year_id || curFy,
        current_amount: p.current_amount,
      })));
      return;
    }
    // Priority 2: suggest one demand row per distinct floor usage (e.g. Residential + Commercial)
    const floors = item?.floors_detail || [];
    if (floors.length > 0) {
      const groups = {};
      floors.forEach((f) => {
        const u = (f.usage_type || "residential").toLowerCase();
        groups[u] = (groups[u] || 0) + (Number(f.super_builtup_area_sqft) || 0);
      });
      setRows(Object.entries(groups).map(([usage, area]) => ({
        usage_label: usage.charAt(0).toUpperCase() + usage.slice(1),
        area_sqft: area,
        tax_head_id: defaultHead,
        financial_year_id: curFy,
        current_amount: "",
      })));
    } else {
      setRows([{ usage_label: "", area_sqft: null, tax_head_id: defaultHead, financial_year_id: curFy, current_amount: "" }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, curFy, defaultHead]);

  const updateRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows([...rows, { usage_label: "", area_sqft: null, tax_head_id: defaultHead, financial_year_id: curFy, current_amount: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const rationaleOk = remarks.trim().length >= MIN_RATIONALE;

  const submit = async () => {
    if (!rationaleOk) {
      toast.error(`A rationale of at least ${MIN_RATIONALE} characters is required — it is stored permanently in the audit trail`);
      return;
    }
    setBusy(true);
    try {
      const body = { action, remarks: remarks.trim() };
      if (action === "approve" && genDemand) {
        const valid = rows.filter((r) => r.tax_head_id && r.financial_year_id && Number(r.current_amount) > 0)
          .map((r) => ({ tax_head_id: r.tax_head_id, financial_year_id: r.financial_year_id,
                         current_amount: Number(r.current_amount), usage_label: r.usage_label || undefined }));
        if (valid.length === 0) {
          toast.error("Add at least one demand row with tax head, FY and amount — or untick demand generation");
          setBusy(false);
          return;
        }
        body.new_demands = valid;
      }
      const url = type === "reassessment" ? `/t/${code}/reassessments/${item.id}/review` : `/t/${code}/surveys/${item.id}/review`;
      const r = await api.put(url, body);
      const nDem = body.new_demands?.length || 0;
      const cert = r.data.certificate_number ? ` • Transfer certificate ${r.data.certificate_number} issued` : "";
      toast.success(`${type === "reassessment" ? "Reassessment" : "Survey"} ${action}d${r.data.pin ? ` • New PIN: ${r.data.pin}` : ""}${nDem ? ` • ${nDem} demand${nDem > 1 ? "s" : ""} generated` : ""}${cert}`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const ai = item?.ai_analysis;

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onDone(false)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-mono text-base">{item?.reassessment_id || item?.survey_id}</DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm bg-muted rounded-lg p-3">
              <div><p className="text-xs text-muted-foreground">Submitted by</p><p className="font-medium"><ByStaff userId={item.initiated_by || item.surveyed_by} name={item.initiated_by_name || item.surveyed_by_name} empCode={item.initiated_by_employee_id || item.surveyed_by_employee_id} size="sm" /></p></div>
              <div><p className="text-xs text-muted-foreground">Date</p><p className="font-medium">{fmtDate(item.created_at)}</p></div>
              {item.pin && <div><p className="text-xs text-muted-foreground">Property PIN</p><p className="font-mono">{item.pin}</p></div>}
              {item.digipin && <div><p className="text-xs text-muted-foreground">DIGIPIN (India Post)</p><p className="font-mono text-primary">{item.digipin}</p></div>}
              {item.owner_name && <div><p className="text-xs text-muted-foreground">Owner</p><p className="font-medium">{item.owner_name}</p></div>}
              {item.super_builtup_area_sqft && (
                <div><p className="text-xs text-muted-foreground">Super built-up</p>
                  <p className="font-medium">{Number(item.super_builtup_area_sqft).toLocaleString("en-IN")} sqft
                    <span className="text-xs text-muted-foreground"> • carpet {Number(item.carpet_area_sqft || Math.round(item.super_builtup_area_sqft * 0.8)).toLocaleString("en-IN")} sqft</span></p></div>
              )}
              {item.total_area_plot_sqft && <div><p className="text-xs text-muted-foreground">Plot area</p><p className="font-medium">{Number(item.total_area_plot_sqft).toLocaleString("en-IN")} sqft</p></div>}
              {item.reason && <div className="col-span-2"><p className="text-xs text-muted-foreground">Reason</p><p>{item.reason}</p></div>}
              {item.address && <div className="col-span-2"><p className="text-xs text-muted-foreground">Address</p><p>{item.address}</p></div>}
              {item.doc_collect_date && (
                <div className="col-span-2" data-testid="review-doc-due">
                  <p className="text-xs text-muted-foreground">Pending documents to be collected on</p>
                  <p className="font-medium">{item.doc_collect_date}</p>
                </div>
              )}
            </div>

            {(item.documents || []).length > 0 && (
              <div className="border rounded-lg p-3 space-y-2" data-testid="review-documents">
                <p className="text-sm font-medium">Documents uploaded by collector</p>
                {(item.documents || []).map((d, i) => (
                  <div key={i} className="text-sm">
                    <p className="text-xs text-muted-foreground">{d.doc_label || "Document"}</p>
                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                      {(d.files || []).map((f) => (
                        <button key={f.file_id} type="button"
                          className="inline-flex items-center gap-1 text-[11px] bg-muted hover:bg-accent rounded px-1.5 py-0.5 max-w-[240px]"
                          onClick={async () => {
                            try {
                              const r = await api.get(`/t/${code}/field/docs/${f.file_id}`, { responseType: "blob" });
                              const url = URL.createObjectURL(r.data);
                              const a = document.createElement("a");
                              a.href = url; a.download = f.filename || "document"; a.click();
                              URL.revokeObjectURL(url);
                            } catch { toast.error("Could not download the document"); }
                          }}
                          data-testid={`review-doc-file-${f.file_id}`}>
                          <span className="truncate">{f.filename}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(item.new_floors?.length > 0 || item.original_floors?.length > 0 || item.ai_analysis) && (
              <AssessmentComparison ai={item.ai_analysis} original={item.original_floors} current={item.new_floors} />
            )}

            {item.assessment && (
              <div className="border rounded-lg p-3 text-sm space-y-1.5 bg-accent/40" data-testid="review-assessment-summary">
                <p className="font-medium">Rate engine assessment (by collector)</p>
                <p className="text-xs text-muted-foreground">
                  Constructed/SBU {Number(item.assessment.total_constructed_area_sqft || 0).toLocaleString("en-IN")} sqft
                  {item.assessment.plot_area_sqft ? ` • Plot ${Number(item.assessment.plot_area_sqft).toLocaleString("en-IN")} sqft` : ""}
                  {" • "}{item.assessment.demand_count} demand group(s)
                </p>
                {(item.assessment.groups || []).map((g, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-card border rounded-md px-2 py-1.5">
                    <span>{g.owner_name} • {g.usage_type_name} • {g.area_sqft} sqft</span>
                    <span className="tabular-nums font-medium">
                      PT ₹{Number(g.property_tax_yearly).toLocaleString("en-IN")}/yr + UC ₹{Number(g.user_fee_yearly).toLocaleString("en-IN")}/yr
                    </span>
                  </div>
                ))}
              </div>
            )}

            {(item.floors_detail || []).length > 0 && (
              <div data-testid="review-floors-detail">
                <p className="text-sm font-medium mb-1 flex items-center gap-1.5"><Layers3 className="h-4 w-4" /> Floor-wise usage</p>
                <div className="border rounded-lg divide-y">
                  {item.floors_detail.map((f, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                      <span className="font-medium">{f.floor_label}</span>
                      <span className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize border ${
                          f.usage_type === "commercial" ? "bg-amber-50 text-amber-700 border-amber-200"
                            : f.usage_type === "mixed" ? "bg-violet-50 text-violet-700 border-violet-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{f.usage_type}</span>
                        <span className="text-muted-foreground text-xs tabular-nums">{Number(f.super_builtup_area_sqft || 0).toLocaleString("en-IN")} sqft</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ai && (
              <div className="border rounded-lg p-3 text-sm space-y-1 bg-accent/40" data-testid="review-ai-summary">
                <p className="font-medium flex items-center gap-1.5"><ScanEye className="h-4 w-4" /> AI vision analysis
                  <span className="text-xs font-normal capitalize px-2 py-0.5 rounded-full bg-card border">{ai.confidence} confidence</span></p>
                <p className="text-xs text-muted-foreground">
                  {ai.floors_count} floor(s) detected • Plot ~{Number(ai.approx_plot_area_sqft || 0).toLocaleString("en-IN")} sqft •
                  SBU ~{Number(ai.approx_super_builtup_area_sqft || 0).toLocaleString("en-IN")} sqft • {ai.construction_type_guess || ""}
                </p>
                {(ai.observations || []).length > 0 && <p className="text-xs text-muted-foreground">{ai.observations.join(" • ")}</p>}
              </div>
            )}

            {type === "reassessment" && item.new_data && Object.keys(item.new_data).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Proposed changes</p>
                <div className="border rounded-lg p-3 text-sm space-y-1">
                  {Object.entries(item.new_data).map(([k, v]) => v ? (
                    <div key={k} className="flex justify-between gap-3">
                      <span className="text-muted-foreground">{k.replace("new_", "").replace(/_/g, " ")}</span>
                      <span className="font-medium text-right">{String(v)}</span>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}
            {(item.photos || []).length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {item.photos.map((p, i) => (
                  <img key={i} src={p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`} alt="site" className="h-20 w-20 object-cover rounded-lg border" />
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label>Decision</Label>
              <div className="flex gap-2">
                <Button variant={action === "approve" ? "default" : "outline"} size="sm" onClick={() => setAction("approve")} data-testid="review-approve-option">Approve</Button>
                <Button variant={action === "reject" ? "destructive" : "outline"} size="sm" onClick={() => setAction("reject")} data-testid="review-reject-option">Reject</Button>
              </div>
            </div>
            {action === "approve" && (
              <div className="border rounded-lg p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={genDemand} onChange={(e) => setGenDemand(e.target.checked)} data-testid="review-generate-demand-checkbox" />
                  Generate demand(s) from this assessment
                </label>
                {genDemand && (
                  <div className="space-y-2" data-testid="review-demand-rows">
                    {(item.floors_detail || []).length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Suggested one demand per usage type from the floor-wise breakup. Set the amount for each.
                      </p>
                    )}
                    {rows.map((r, i) => (
                      <div key={i} className="border rounded-lg p-2.5 space-y-2" data-testid={`demand-row-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <Input className="h-9 max-w-[180px]" placeholder="Usage label (optional)" value={r.usage_label}
                            onChange={(e) => updateRow(i, { usage_label: e.target.value })} data-testid={`demand-usage-input-${i}`} />
                          {r.area_sqft ? <span className="text-xs text-muted-foreground tabular-nums">{Number(r.area_sqft).toLocaleString("en-IN")} sqft SBU</span> : null}
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => removeRow(i)}
                            data-testid={`demand-row-remove-${i}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <Select value={r.tax_head_id} onValueChange={(v) => updateRow(i, { tax_head_id: v })}>
                            <SelectTrigger className="h-9" data-testid={`demand-taxhead-select-${i}`}><SelectValue placeholder="Tax head" /></SelectTrigger>
                            <SelectContent>{taxHeads.map((t) => <SelectItem key={t.id} value={t.id}>{t.tax_head_name}</SelectItem>)}</SelectContent>
                          </Select>
                          <Select value={r.financial_year_id} onValueChange={(v) => updateRow(i, { financial_year_id: v })}>
                            <SelectTrigger className="h-9"><SelectValue placeholder="Financial year" /></SelectTrigger>
                            <SelectContent>{fys.map((f) => <SelectItem key={f.id} value={f.id}>{f.fy_label}</SelectItem>)}</SelectContent>
                          </Select>
                          <Input type="number" className="h-9" placeholder="Amount (INR)" value={r.current_amount}
                            onChange={(e) => updateRow(i, { current_amount: e.target.value })} data-testid={`demand-amount-input-${i}`} />
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="add-demand-row-button">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add demand row
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1">
              <Label className="flex items-center justify-between">
                <span>{action === "approve" ? "Approval rationale" : "Rejection rationale"} <span className="text-destructive">*</span></span>
                <span className={`text-[11px] font-normal ${rationaleOk ? "text-muted-foreground" : "text-amber-700"}`} data-testid="review-rationale-counter">
                  {remarks.trim().length}/{MIN_RATIONALE} min
                </span>
              </Label>
              <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                placeholder={action === "approve" ? "Why is this being approved? e.g. Documents verified, site inspected on …" : "Why is this being rejected? Shared with the submitting staff"}
                aria-invalid={remarks.length > 0 && !rationaleOk} data-testid="review-remarks-input" />
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Recorded permanently with your name, role and the exact time (audit trail).
              </p>
            </div>
            <Button className="w-full" onClick={submit} disabled={busy || !rationaleOk} data-testid="review-submit-button">
              {busy ? "Submitting..." : `Confirm ${action}`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Approvals filters (Phase 55) ----------
const EMPTY_FILTERS = { q: "", status: "all", by: "all", from: "", to: "", source: "all" };

// Phase 95: "Submitted by" filters by staff *id* when the record carries one (two staff with the same
// name stay distinct); legacy rows without an id fall back to the name.
export const submitterKey = (it) => it.initiated_by || it.surveyed_by || it.initiated_by_name || it.surveyed_by_name || "";
export const submitterName = (it) => it.initiated_by_name || it.surveyed_by_name || "";

export const filterApprovalItems = (items, f) => {
  if (!items) return items;
  return items.filter((it) => {
    if (f.q) {
      const hay = [it.reassessment_id, it.survey_id, it.transfer_id, it.pin, it.owner_name,
        it.old_owner_name, it.new_owner_name, it.initiated_by_name, it.surveyed_by_name, it.reason]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(f.q.toLowerCase())) return false;
    }
    if (f.status !== "all" && it.status !== f.status) return false;
    if (f.by !== "all" && submitterKey(it) !== f.by) return false;
    if (f.source !== "all") {
      const src = it.source === "self_assessment" ? "self_assessment" : "field";
      if (src !== f.source) return false;
    }
    const d = (it.created_at || "").slice(0, 10);
    if (f.from && d < f.from) return false;
    if (f.to && d > f.to) return false;
    return true;
  });
};

function ApprovalFilters({ items, value, onChange, showSource = false, testIdPrefix }) {
  const { code } = useRouteParams();
  const statuses = [...new Set((items || []).map((i) => i.status).filter(Boolean))];
  const submitterMap = new Map();
  (items || []).forEach((i) => { const k = submitterKey(i); if (k && submitterName(i) && !submitterMap.has(k)) submitterMap.set(k, { id: i.initiated_by || i.surveyed_by, name: submitterName(i) }); });
  const submitters = [...submitterMap.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  const active = value.q || value.status !== "all" || value.by !== "all" || value.from || value.to || value.source !== "all";
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Card className="shadow-sm mb-3" data-testid={`${testIdPrefix}-bar`}>
      <CardContent className="p-3 flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground pb-2.5 pr-1">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        <div className="flex-1 min-w-[180px] space-y-1">
          <Label className="text-xs">Search</Label>
          <Input className="h-9" placeholder="ID, PIN, owner, collector..." value={value.q}
            onChange={(e) => set({ q: e.target.value })} data-testid={`${testIdPrefix}-search-input`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={value.status} onValueChange={(v) => set({ status: v })}>
            <SelectTrigger className="h-9 w-[150px]" data-testid={`${testIdPrefix}-status-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statuses.map((s) => (
                <SelectItem key={s} value={s} className="capitalize" data-testid={`${testIdPrefix}-status-option-${s}`}>
                  {s.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {showSource && (
          <div className="space-y-1">
            <Label className="text-xs">Type</Label>
            <Select value={value.source} onValueChange={(v) => set({ source: v })}>
              <SelectTrigger className="h-9 w-[185px]" data-testid={`${testIdPrefix}-source-select`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="field">Field survey</SelectItem>
                <SelectItem value="self_assessment">Self-assessment (citizen)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Submitted by</Label>
          <Select value={value.by} onValueChange={(v) => set({ by: v })}>
            <SelectTrigger className="h-9 w-[170px]" data-testid={`${testIdPrefix}-by-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {submitters.map(([k, s]) => <SelectItem key={k} value={k}><StaffLabel code={code} userId={s.id} name={s.name} /></SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" className="h-9 w-[145px]" value={value.from}
            onChange={(e) => set({ from: e.target.value })} data-testid={`${testIdPrefix}-from-input`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-[145px]" value={value.to}
            onChange={(e) => set({ to: e.target.value })} data-testid={`${testIdPrefix}-to-input`} />
        </div>
        {active && (
          <Button variant="ghost" size="sm" className="h-9 text-muted-foreground"
            onClick={() => onChange({ ...EMPTY_FILTERS })} data-testid={`${testIdPrefix}-clear-button`}>
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Bulk approve / reject (Phase 56 + 57) ----------
// Processes the selected pending cases one by one through the SAME review API a
// Phase 90: Who / When / Why of a decision, rendered under decided cases
export function DecisionLine({ item }) {
  const approved = item.status === "approved";
  const when = item.reviewed_at || item.updated_at || item.review_date;
  return (
    <div className={`mt-1.5 rounded-md border px-2.5 py-1.5 text-xs ${approved ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}
      data-testid={`decision-line-${item.id}`}>
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <span className={`font-semibold ${approved ? "text-emerald-800" : "text-red-800"}`}>{approved ? "Approved" : "Rejected"}</span>
        <span className="text-muted-foreground">by</span>
        <ByStaff userId={item.reviewed_by} name={item.reviewed_by_name} empCode={item.reviewed_by_employee_id} size="xs" />
        {item.reviewed_by_role_label && <span className="text-muted-foreground">({item.reviewed_by_role_label})</span>}
        <span className="text-muted-foreground">•</span>
        <span className="tabular-nums" data-testid={`decision-when-${item.id}`}>{item.reviewed_at ? fmtDateTime(item.reviewed_at) : fmtDate(when)}</span>
      </p>
      {(item.review_rationale || item.review_remarks) && (
        <p className="mt-0.5 text-foreground/90" data-testid={`decision-why-${item.id}`}>
          <span className="text-muted-foreground">Rationale:</span> {item.review_rationale || item.review_remarks}
        </p>
      )}
    </div>
  );
}

// single review uses. For approvals, demands are auto-generated from each case's
// collector rate-engine proposal (proposed_demands) when available. For
// rejections, a shared reason is mandatory and no demands are generated.
function BulkActionDialog({ code, type, action, items, taxHeads, fys, onClose, onDone }) {
  const isReject = action === "reject";
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const curFy = fys.find((f) => f.is_current)?.id || "";

  const buildDemands = (it) => (it.proposed_demands || []).map((p) => ({
    tax_head_id: p.tax_head_id || taxHeads.find((t) => t.tax_code === p.tax_code)?.id,
    financial_year_id: p.financial_year_id || it.assessment?.financial_year_id || curFy,
    current_amount: Number(p.current_amount),
    usage_label: p.usage_label || undefined,
  })).filter((d) => d.tax_head_id && d.financial_year_id && d.current_amount > 0);

  const withProposals = items.filter((it) => buildDemands(it).length > 0).length;

  const rationaleOk = remarks.trim().length >= MIN_RATIONALE;

  const run = async () => {
    if (!rationaleOk) {
      toast.error(`A ${isReject ? "rejection" : "approval"} rationale of at least ${MIN_RATIONALE} characters is required — it is stored permanently in the audit trail`);
      return;
    }
    setBusy(true);
    const out = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const label = it.reassessment_id || it.survey_id;
      try {
        const body = { action, remarks: remarks.trim() };
        if (!isReject) {
          const demands = buildDemands(it);
          if (demands.length > 0) body.new_demands = demands;
          const r = await api.put(type === "reassessment"
            ? `/t/${code}/reassessments/${it.id}/review`
            : `/t/${code}/surveys/${it.id}/review`, body);
          out.push({ label, ok: true, demands: demands.length, pin: r.data?.pin });
        } else {
          await api.put(type === "reassessment"
            ? `/t/${code}/reassessments/${it.id}/review`
            : `/t/${code}/surveys/${it.id}/review`, body);
          out.push({ label, ok: true });
        }
      } catch (err) {
        out.push({ label, ok: false, error: errMsg(err) });
      }
      setProgress(i + 1);
    }
    setResults(out);
    setBusy(false);
  };

  const okCount = (results || []).filter((r) => r.ok).length;
  const failCount = (results || []).filter((r) => !r.ok).length;
  const noun = `${type === "reassessment" ? "reassessment" : "survey"}${items.length > 1 ? "s" : ""}`;

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) (results ? onDone() : onClose()); }}>
      <DialogContent className="max-w-lg" data-testid={`bulk-${action}-dialog`}>
        <DialogHeader>
          <DialogTitle className={`font-heading flex items-center gap-2 ${isReject ? "text-red-700" : ""}`}>
            {isReject ? <XCircle className="h-5 w-5" /> : <CheckCheck className="h-5 w-5" />}
            Bulk {isReject ? "reject" : "approve"} {items.length} {noun}
          </DialogTitle>
        </DialogHeader>

        {!results && !busy && (
          <div className="space-y-3">
            {isReject ? (
              <div className="text-sm bg-red-50 border border-red-200 text-red-900 rounded-lg p-3">
                All {items.length} selected case(s) will be <span className="font-semibold">rejected</span>. The submitting staff can see the shared reason below. No demands are generated.
              </div>
            ) : (
              <div className="text-sm bg-accent/50 border rounded-lg p-3 space-y-1">
                <p><span className="font-semibold">{withProposals}</span> of {items.length} case(s) carry a collector rate-engine proposal — demands will be <span className="font-semibold">generated automatically</span> from those amounts.</p>
                {withProposals < items.length && (
                  <p className="text-xs text-muted-foreground">
                    The remaining {items.length - withProposals} case(s) will be approved without new demand generation (you can add demands later from Properties).
                  </p>
                )}
              </div>
            )}
            <div className="max-h-36 overflow-y-auto border rounded-lg divide-y" data-testid="bulk-action-preview">
              {items.map((it) => (
                <div key={it.id} className="px-3 py-1.5 text-sm flex items-center justify-between gap-2">
                  <span className="font-mono text-xs">{it.reassessment_id || it.survey_id}</span>
                  <span className="text-xs text-muted-foreground truncate">{it.owner_name || it.pin || ""}{!isReject && buildDemands(it).length ? ` • ${buildDemands(it).length} demand(s)` : ""}</span>
                </div>
              ))}
            </div>
            <div className="space-y-1">
              <Label className="flex items-center justify-between">
                <span>{isReject ? "Rejection rationale" : "Approval rationale"} <span className="text-destructive">*</span> <span className="text-muted-foreground font-normal">(applied to all)</span></span>
                <span className={`text-[11px] font-normal ${rationaleOk ? "text-muted-foreground" : "text-amber-700"}`} data-testid="bulk-rationale-counter">{remarks.trim().length}/{MIN_RATIONALE} min</span>
              </Label>
              <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                placeholder={isReject ? "e.g. Documents incomplete — resubmit with ownership proof" : "e.g. Verified in weekly review meeting"}
                data-testid="bulk-action-remarks-input" />
            </div>
            <Button className="w-full" variant={isReject ? "destructive" : "default"} onClick={run} disabled={!rationaleOk}
              data-testid="bulk-action-confirm-button">
              {isReject ? `Reject all ${items.length} case(s)` : `Approve all ${items.length} case(s)`}
            </Button>
          </div>
        )}

        {busy && (
          <div className="space-y-3 py-2" data-testid="bulk-action-progress">
            <p className="text-sm">{isReject ? "Rejecting" : "Approving"} {Math.min(progress + 1, items.length)} of {items.length}…</p>
            <Progress value={(progress / items.length) * 100} />
            <p className="text-xs text-muted-foreground">Please keep this window open until it finishes.</p>
          </div>
        )}

        {results && (
          <div className="space-y-3" data-testid="bulk-action-results">
            <div className="flex items-center gap-3 text-sm">
              <span className={`inline-flex items-center gap-1 font-medium ${isReject ? "text-red-700" : "text-emerald-700"}`}>
                {isReject ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />} {okCount} {isReject ? "rejected" : "approved"}
              </span>
              {failCount > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                  <XCircle className="h-4 w-4" /> {failCount} failed
                </span>
              )}
            </div>
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
              {results.map((r, i) => (
                <div key={i} className="px-3 py-1.5 text-sm flex items-center justify-between gap-2" data-testid={`bulk-result-${r.label}`}>
                  <span className="font-mono text-xs flex items-center gap-1.5">
                    {r.ok ? (isReject ? <XCircle className="h-3.5 w-3.5 text-red-600" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />)
                      : <XCircle className="h-3.5 w-3.5 text-amber-600" />}
                    {r.label}
                  </span>
                  <span className="text-xs text-muted-foreground truncate max-w-[240px]" title={r.error || ""}>
                    {r.ok ? (isReject ? "rejected" : `${r.pin ? `New PIN ${r.pin} • ` : ""}${r.demands ? `${r.demands} demand(s)` : "approved"}`) : r.error}
                  </span>
                </div>
              ))}
            </div>
            <Button className="w-full" onClick={onDone} data-testid="bulk-action-done-button">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const isPendingCase = (it) => it.status === "submitted" || it.status === "under_review";

/**
 * Phase 83: paginated case list (client-side — the filters + bulk selection above it
 * operate on the whole filtered set, only the rendered cards are paged).
 * "Select all pending" still selects every pending case in the filtered set.
 */
function ApprovalList({ items, total, type, selection = [], setSelection, onReview, onBulk, readOnly = false }) {
  const { code } = useRouteParams();
  const paged = usePageSlice(items || [], 20);
  const filterKey = items ? items.length : -1;
  const { resetPage } = paged;
  useEffect(() => { resetPage(); }, [filterKey, resetPage]);
  if (!items) return <CardListSkeleton rows={5} testId={`${type}-skeleton`} />;
  if (items.length === 0) {
    return <EmptyState title={total > 0 ? "No results match the filters" : `No ${type}s submitted`}
      subtitle={total > 0 ? "Try adjusting or clearing the filters above" : undefined} />;
  }
  const pendingIds = items.filter(isPendingCase).map((it) => it.id);
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selection.includes(id));
  const toggleAll = () => setSelection(allSelected ? [] : pendingIds);
  const toggleOne = (id) => setSelection(selection.includes(id) ? selection.filter((x) => x !== id) : [...selection, id]);
  const selectedItems = items.filter((it) => selection.includes(it.id) && isPendingCase(it));
  return (
    <div className="space-y-3" data-testid={`${type}-list`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground" data-testid={`${type}-result-count`}>
          Showing {items.length} of {total}
        </p>
        {pendingIds.length > 0 && !readOnly && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid={`${type}-select-all`} />
            Select all pending ({pendingIds.length})
          </label>
        )}
      </div>
      {selectedItems.length > 0 && !readOnly && (
        <div className="flex items-center justify-between gap-3 border border-primary/30 bg-accent/60 rounded-lg px-3 py-2"
          data-testid={`${type}-bulk-bar`}>
          <p className="text-sm font-medium">{selectedItems.length} case(s) selected</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-8 text-muted-foreground" onClick={() => setSelection([])}
              data-testid={`${type}-bulk-clear-button`}>
              Clear
            </Button>
            <Button variant="destructive" size="sm" className="h-8" onClick={() => onBulk("reject", selectedItems)}
              data-testid={`${type}-bulk-reject-button`}>
              <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject selected
            </Button>
            <Button size="sm" className="h-8" onClick={() => onBulk("approve", selectedItems)}
              data-testid={`${type}-bulk-approve-button`}>
              <CheckCheck className="h-3.5 w-3.5 mr-1.5" /> Approve selected
            </Button>
          </div>
        </div>
      )}
      {paged.slice.map((it) => (
        <Card key={it.id} className={`shadow-sm ${selection.includes(it.id) ? "border-primary/50" : ""}`}>
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              {isPendingCase(it) && !readOnly && (
                <Checkbox checked={selection.includes(it.id)} onCheckedChange={() => toggleOne(it.id)}
                  data-testid={`bulk-select-${it.id}`} />
              )}
              <div className="min-w-0">
                <p className="font-mono text-sm font-medium">{it.reassessment_id || it.survey_id}</p>
                <p className="text-sm">{it.owner_name || it.pin} {it.reason ? `• ${it.reason}` : ""}</p>
                <p className="text-xs text-muted-foreground">By <ByStaff userId={it.initiated_by || it.surveyed_by} name={it.initiated_by_name || it.surveyed_by_name} empCode={it.initiated_by_employee_id || it.surveyed_by_employee_id} /> • {fmtDate(it.created_at)}{it.source === "self_assessment" ? " • Self-assessment (citizen)" : ""}{it.approver_name ? <> • Assigned to <StaffLabel code={code} userId={it.approver_id} name={it.approver_name} /></> : ""}</p>
                {(it.status === "approved" || it.status === "rejected") && it.reviewed_by_name && (
                  <DecisionLine item={it} />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={it.status} />
              {isPendingCase(it) && !readOnly && (
                <Button size="sm" onClick={() => onReview(it)} data-testid={`review-button-${it.id}`}>Review</Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
      {items.length > paged.limit && (
        <PaginationBar page={paged.page} limit={paged.limit} total={paged.total}
          onPageChange={paged.setPage} onLimitChange={paged.setLimit} sizes={[20, 50, 100]} testId={`${type}-pagination`} />
      )}
    </div>
  );
}


function TransferList({ transfers, filtered }) {
  const { code } = useRouteParams();
  const ft = filtered || [];
  const paged = usePageSlice(ft, 20);
  const { resetPage } = paged;
  useEffect(() => { resetPage(); }, [ft.length, resetPage]);
  if (!transfers) return <CardListSkeleton rows={4} testId="transfer-skeleton" />;
  if (transfers.length === 0) return <EmptyState title="No transfer cases recorded" />;
  if (ft.length === 0) return <EmptyState title="No results match the filters" subtitle="Try adjusting or clearing the filters above" />;
  return (
    <div className="space-y-3" data-testid="transfer-cases-list">
      <p className="text-xs text-muted-foreground" data-testid="transfer-result-count">
        Showing {ft.length} of {transfers.length}
      </p>
      {paged.slice.map((t) => (
        <Card key={t.id} className="shadow-sm" data-testid={`transfer-case-${t.transfer_id}`}>
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-mono text-sm font-medium">{t.transfer_id} <span className="font-sans text-xs text-muted-foreground">• via {t.reassessment_id}</span></p>
              <p className="text-sm">
                <span className="font-mono text-xs">{t.pin}</span> — {t.old_owner_name} <span className="text-muted-foreground">→</span> <span className="font-medium">{t.new_owner_name}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                By <ByStaff userId={t.initiated_by} name={t.initiated_by_name} empCode={t.initiated_by_employee_id} /> • {fmtDate(t.created_at)}{t.remarks ? ` • ${t.remarks}` : ""}
              </p>
              {t.status === "approved" && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                  <CertificateChip certificateNumber={t.certificate_number} testId={`transfer-certificate-chip-${t.transfer_id}`} />
                  {t.certificate_number ? `issued ${fmtDate(t.certificate_issued_at)}` : "Certificate is issued on first download"}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {t.status === "approved" && (
                <>
                  <CertificateVerifyLink code={code} token={t.certificate_token} testId={`transfer-certificate-verify-${t.transfer_id}`} />
                  <CertificateDownloadButton code={code} caseRef={t.id} certificateNumber={t.certificate_number}
                    testId={`transfer-certificate-download-${t.transfer_id}`} />
                </>
              )}
              <StatusBadge status={t.status} />
            </div>
          </CardContent>
        </Card>
      ))}
      {ft.length > paged.limit && (
        <PaginationBar page={paged.page} limit={paged.limit} total={paged.total}
          onPageChange={paged.setPage} onLimitChange={paged.setLimit} sizes={[20, 50, 100]} testId="transfer-pagination" />
      )}
    </div>
  );
}

// Phase 152: Approvals tabs <-> "approvals" module sub-features
const TABS = [
  { value: "reassessments", feature: "reassessments" },
  { value: "surveys", feature: "surveys" },
  { value: "saf", feature: "saf" },   // Phase 207
  { value: "transfers", feature: "transfers" },
  { value: "targets", feature: "target_proposals" },
];

export default function Approvals() {
  const { code } = useParams();
  const access = useRoleModules(code);
  const show = (f) => access.featureAllowed("approvals", f);
  const ro = (f) => !access.canWrite("approvals", f);
  const visibleTabs = TABS.filter((t) => show(t.feature));
  const [tab, setTab] = useState("reassessments");
  useEffect(() => {
    if (!access.loaded) return;
    if (!visibleTabs.some((t) => t.value === tab)) { const f = firstAllowedTab(access, "approvals", TABS); if (f) setTab(f); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.loaded, visibleTabs.length]);
  const [reassessments, setReassessments] = useState(null);
  const [surveys, setSurveys] = useState(null);
  const [taxHeads, setTaxHeads] = useState([]);
  const [fys, setFys] = useState([]);
  const [transfers, setTransfers] = useState(null);
  const [review, setReview] = useState(null); // {item, type}
  // per-tab filters (Phase 55)
  const [rf, setRf] = useState({ ...EMPTY_FILTERS });
  const [sf, setSf] = useState({ ...EMPTY_FILTERS });
  const [tf, setTf] = useState({ ...EMPTY_FILTERS });
  // bulk approve selection (Phase 56)
  const [selR, setSelR] = useState([]);
  const [selS, setSelS] = useState([]);
  const [bulk, setBulk] = useState(null); // {type, items}
  const [pendingTargets, setPendingTargets] = useState(null); // Phase 81: collector target proposals
  // Phase 207: citizen self-assessments (final approval + PIN)
  const [safOpen, setSafOpen] = useState(null);
  const [safReload, setSafReload] = useState(0);
  const [safPending, setSafPending] = useState(null);
  const loadSafPending = useCallback(() =>
    api.get(`/t/${code}/saf`, { params: { tab: "pending", page: 1, limit: 1 } })
      .then((r) => setSafPending(r.data?.counts?.pending_for_me ?? null)).catch(() => setSafPending(null)), [code]);

  const loadPendingTargets = useCallback(() =>
    api.get(`/t/${code}/targets/proposals`, { params: { status: "pending" } })
      .then((r) => setPendingTargets((r.data.counts || {}).pending ?? r.data.proposals.length))
      .catch(() => setPendingTargets(null)), [code]);

  const load = useCallback(async () => {
    // Phase 152: lists the viewer has no access to are never requested (the API would answer 403)
    const [r, s, t] = await Promise.all([
      show("reassessments") ? api.get(`/t/${code}/reassessments`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      show("surveys") ? api.get(`/t/${code}/surveys`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      show("transfers") ? api.get(`/t/${code}/transfer-cases`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setReassessments(r.data);
    setSurveys(s.data);
    setTransfers(t.data);
    if (show("target_proposals")) loadPendingTargets();
    if (show("saf")) loadSafPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, loadPendingTargets, loadSafPending, access.loaded]);

  useEffect(() => {
    load().catch(() => {});
    api.get(`/t/${code}/masters/tax-heads`).then((r) => setTaxHeads(r.data)).catch(() => {});
    api.get(`/t/${code}/masters/financial-years`).then((r) => setFys(r.data)).catch(() => {});
  }, [load, code]);

  const List = ({ items, total, type, selection = [], setSelection, readOnly = false }) => (
    <ApprovalList items={items} total={total} type={type} selection={selection} setSelection={setSelection} readOnly={readOnly}
      onReview={(it) => setReview({ item: it, type })} onBulk={(action, selectedItems) => setBulk({ type, items: selectedItems, action })} />
  );

  return (
    <div>
      <PageHeader title="Approvals" subtitle="Review reassessments, new surveys and self-assessments" />
      <div className="mb-4">
        <MissingRatesBanner code={code} />
      </div>
      {access.loaded && visibleTabs.length === 0 ? <NoAccessPanel what="Approvals" testId="approvals-no-access" /> : (
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {show("reassessments") && <TabsTrigger value="reassessments" data-testid="tab-reassessments">
            Reassessments {reassessments && `(${reassessments.filter((r) => r.status === "submitted").length} pending)`}{ro("reassessments") && <ViewOnlyBadge />}
          </TabsTrigger>}
          {show("surveys") && <TabsTrigger value="surveys" data-testid="tab-surveys">
            Surveys &amp; Self-Assessments {surveys && `(${surveys.filter((s) => s.status === "submitted").length} pending)`}{ro("surveys") && <ViewOnlyBadge />}
          </TabsTrigger>}
          {show("saf") && <TabsTrigger value="saf" data-testid="tab-saf">
            Self-Assessments (SAF) {safPending != null && `(${safPending} pending)`}{ro("saf") && <ViewOnlyBadge />}
          </TabsTrigger>}
          {show("transfers") && <TabsTrigger value="transfers" data-testid="tab-transfers">
            Transfer Cases {transfers && `(${transfers.filter((t) => t.status === "pending").length} pending)`}
          </TabsTrigger>}
          {show("target_proposals") && <TabsTrigger value="targets" data-testid="tab-target-proposals">
            Collector Targets {pendingTargets != null && `(${pendingTargets} pending)`}{ro("target_proposals") && <ViewOnlyBadge />}
          </TabsTrigger>}
        </TabsList>
        <TabsContent value="reassessments" className="mt-4">
          {ro("reassessments") && <ReadOnlyNotice what="reassessment cases" testId="reassessments-readonly-notice" className="mb-3" />}
          <ApprovalFilters items={reassessments} value={rf} onChange={setRf} testIdPrefix="reassess-filter" />
          <List items={filterApprovalItems(reassessments, rf)} total={reassessments?.length || 0} type="reassessment"
            selection={selR} setSelection={setSelR} readOnly={ro("reassessments")} />
        </TabsContent>
        <TabsContent value="surveys" className="mt-4">
          {ro("surveys") && <ReadOnlyNotice what="survey cases" testId="surveys-readonly-notice" className="mb-3" />}
          <ApprovalFilters items={surveys} value={sf} onChange={setSf} showSource testIdPrefix="survey-filter" />
          <List items={filterApprovalItems(surveys, sf)} total={surveys?.length || 0} type="survey"
            selection={selS} setSelection={setSelS} readOnly={ro("surveys")} />
        </TabsContent>
        <TabsContent value="saf" className="mt-4" data-testid="saf-approvals-tab">
          {ro("saf") && <ReadOnlyNotice what="citizen self-assessments" testId="saf-readonly-notice" className="mb-3" />}
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <SafQueue code={code} onOpen={setSafOpen} reloadKey={safReload} testIdPrefix="admin-saf" pendingLabel="Pending final approval" limit={10} showEscalated />
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground mt-3">
            Citizen self-assessments flow Citizen → Field Collector (physical verification, final demand) → Supervisor → Municipal Admin.
            Final approval generates the Property PIN, creates the property with its demand and links the PIN to the citizen's account.
            You can also assign or reassign the Field Collector while a case is open. Every step is recorded in Reports → SAF Approval Log.
            Cases nobody acts on within the time limit (Settings → SAF auto-escalation) move to the next role automatically and are tagged <b>Auto-escalated</b>;
            you may also decide any case directly with a reason, even while it is still with the Field Collector.
          </p>
        </TabsContent>
        <TabsContent value="transfers" className="mt-4">
          <ApprovalFilters items={transfers} value={tf} onChange={setTf} testIdPrefix="transfer-filter" />
          <TransferList transfers={transfers} filtered={filterApprovalItems(transfers, tf)} />
          <p className="text-xs text-muted-foreground mt-3">
            Transfer cases follow their reassessment: approving the reassessment applies the new owner and marks the transfer approved.
          </p>
        </TabsContent>
        <TabsContent value="targets" className="mt-4" data-testid="target-proposals-tab">
          {ro("target_proposals") && <ReadOnlyNotice what="collector target proposals" testId="target-proposals-readonly-notice" className="mb-3" />}
          <TargetProposalsPanel code={code} onChanged={loadPendingTargets} readOnly={ro("target_proposals")} />
          <p className="text-xs text-muted-foreground mt-3">
            Supervisors with <b>View &amp; edit</b> on the Team Scorecard module propose collector targets here; approving writes the target
            immediately and notifies the supervisor. Set targets yourself under Users &amp; Staff → Collector targets.
          </p>
        </TabsContent>
      </Tabs>
      )}
      {review && (
        <ReviewDialog code={code} item={review.item} type={review.type} taxHeads={taxHeads} fys={fys}
          onDone={() => { setReview(null); load(); }} />
      )}
      {bulk && (
        <BulkActionDialog code={code} type={bulk.type} action={bulk.action || "approve"} items={bulk.items}
          taxHeads={taxHeads} fys={fys}
          onClose={() => setBulk(null)}
          onDone={() => { setBulk(null); setSelR([]); setSelS([]); load(); }} />
      )}
      <SafCaseDialog code={code} saf={safOpen} role="municipal_admin" open={!!safOpen} onClose={() => setSafOpen(null)}
        onChanged={() => { setSafReload((k) => k + 1); loadSafPending(); }} testIdPrefix="admin-saf-case" />
    </div>
  );
}
